import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { useShops } from '../../hooks/useShops';
import { usePaymentMethods } from '../../hooks/usePaymentMethods';

interface AdditionalFiltersProps {
  onShopChange?: (shopId: string) => void;
  onPaymentMethodChange?: (paymentMethodId: string) => void;
  selectedShop?: string;
  selectedPaymentMethod?: string;
  className?: string;
}

export const AdditionalFilters = ({
  onShopChange,
  onPaymentMethodChange,
  selectedShop = '',
  selectedPaymentMethod = '',
  className = '',
}: AdditionalFiltersProps) => {
  const { t } = useTranslation('productsWithoutStockModule');
  const { shops, isLoading: shopsLoading } = useShops();
  const { paymentMethods, isLoading: paymentMethodsLoading } = usePaymentMethods();

  // 🎯 Hook useQueryParams para sincronizar con URL
  const { params } = useQueryParams({
    shop: { type: 'string', default: '' },
    paymentMethod: { type: 'string', default: '' },
  });

  // 🎯 Efecto para sincronizar con cambios en URL
  useEffect(() => {
    const shopFromUrl = params.shop ?? '';
    const paymentMethodFromUrl = params.paymentMethod ?? '';

    if (shopFromUrl !== selectedShop) {
      onShopChange?.(shopFromUrl);
    }
    if (paymentMethodFromUrl !== selectedPaymentMethod) {
      onPaymentMethodChange?.(paymentMethodFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.shop, params.paymentMethod]);

  // Detectar si es mobile (tailwind breakpoint sm)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(max-width: 640px)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) =>
      setIsMobile('matches' in e ? e.matches : (e as MediaQueryList).matches);
    handler(mql);
    mql.addEventListener?.('change', handler as (e: MediaQueryListEvent) => void);
    // Compatibilidad con navegadores antiguos
    mql.addListener?.(handler as (this: MediaQueryList, ev: MediaQueryListEvent) => any);
    return () => {
      mql.removeEventListener?.('change', handler as (e: MediaQueryListEvent) => void);
      mql.removeListener?.(handler as (this: MediaQueryList, ev: MediaQueryListEvent) => any);
    };
  }, []);

  // Preparar opciones para los comboboxes
  const shopOptions = [
    { label: t('filters.all', { defaultValue: 'Todos' }), value: '' },
    ...shops.map((shop) => ({
      label: shop.name,
      value: shop.id.toString(),
    })),
  ];

  const paymentMethodOptions = [
    { label: t('filters.all', { defaultValue: 'Todos' }), value: '' },
    ...paymentMethods.map((method) => ({
      label: method.method,
      value: method.id.toString(),
    })),
  ];

  const handleShopChange = useCallback(
    (value: string) => {
      onShopChange?.(value);
    },
    [onShopChange],
  );

  const handlePaymentMethodChange = useCallback(
    (value: string) => {
      onPaymentMethodChange?.(value);
    },
    [onPaymentMethodChange],
  );

  // Placeholders según viewport
  const storePlaceholder = isMobile ? t('filters.selectStoreShort') : t('filters.selectStore');
  const paymentPlaceholder = isMobile ? t('filters.selectPaymentMethodShort') : t('filters.selectPaymentMethod');

  // Clases de estilo para placeholder/borde en light mode
  const lightBorderClass = 'light:!border-lines';
  const lightPlaceholderClass = 'light:!text-lines';

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Selector de Tienda */}
      <div className="flex-1 min-w-[100px] sm:min-w-[200px] sm:flex-none">
        <Combobox
          options={shopOptions}
          value={selectedShop}
          setValue={handleShopChange}
          placeholder={storePlaceholder}
          disabled={shopsLoading}
          dataTestId="shop-select"
          buttonClassName={lightBorderClass}
          placeholderClassName={lightPlaceholderClass}
        />
      </div>

      {/* Selector de Método de Pago */}
      <div className="flex-1 min-w-[100px] sm:min-w-[200px] sm:flex-none">
        <Combobox
          options={paymentMethodOptions}
          value={selectedPaymentMethod}
          setValue={handlePaymentMethodChange}
          placeholder={paymentPlaceholder}
          disabled={paymentMethodsLoading}
          dataTestId="payment-method-select"
          buttonClassName={lightBorderClass}
          placeholderClassName={lightPlaceholderClass}
        />
      </div>
    </div>
  );
};
